import React, { useState } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

// shadcn Table look + TanStack · compact 36 / comfortable 44 · sticky header · keyboard row focus (J/K) · checked rows → BulkBar
export function DataTable({ columns, data, density = "compact", selectable = false, onRowClick, getRowId, bulkActions = [], emptyState, testid = "data-table", groupBy }) {
  const [sorting, setSorting] = useState([]);
  const [selected, setSelected] = useState({});
  const [cursor, setCursor] = useState(-1);
  const table = useReactTable({ data, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getRowId });
  const rows = table.getRowModel().rows;
  const rowH = density === "compact" ? "h-row" : "h-ctl-touch";
  const nSel = Object.values(selected).filter(Boolean).length;
  const onKey = (e) => {
    if (e.key === "j" || e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
    if (e.key === "k" || e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === "Enter" && rows[cursor]) onRowClick?.(rows[cursor].original);
    if (e.key === "x" && rows[cursor] && selectable) setSelected((s) => ({ ...s, [rows[cursor].id]: !s[rows[cursor].id] }));
  };
  let lastGroup = null;
  return (
    <div className="font-ui relative" data-testid={testid} tabIndex={0} onKeyDown={onKey}>
      <table className="w-full text-ui-sm border-collapse">
        <thead className="sticky top-0 bg-card z-10">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border text-ui-xs text-muted-foreground">
              {selectable && <th className="w-8 pl-4" />}
              {hg.headers.map((h) => (
                <th key={h.id} onClick={h.column.getToggleSortingHandler()} className={cn("text-left font-medium px-3 first:pl-4 h-row select-none border-r border-hairline last:border-r-0", h.column.getCanSort() && "cursor-pointer hover:text-foreground")}>
                  <span className="inline-flex items-center gap-1">{flexRender(h.column.columnDef.header, h.getContext())}
                    {h.column.getIsSorted() === "asc" && <ChevronUp className="w-3 h-3" />}{h.column.getIsSorted() === "desc" && <ChevronDown className="w-3 h-3" />}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="py-10">{emptyState}</td></tr>}
          {rows.map((r, i) => {
            const g = groupBy ? groupBy(r.original) : null;
            const header = g && g.key !== lastGroup ? (lastGroup = g.key, g) : null;
            const isSel = i === cursor;
            return (
              <React.Fragment key={r.id}>
                {header && <tr className="bg-muted"><td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 h-row text-ui-xs text-muted-foreground font-medium">{header.label}</td></tr>}
                <tr onClick={() => onRowClick?.(r.original)} data-testid={`${testid}-row-${r.id}`}
                  className={cn("border-b border-hairline transition-colors duration-120 cursor-pointer hover:bg-muted", rowH, isSel && "bg-accent shadow-[inset_2px_0_0_hsl(var(--primary))]", selected[r.id] && "bg-accent/60")}>
                  {selectable && <td className="pl-4" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))} className="accent-[hsl(var(--primary))] w-4 h-4" /></td>}
                  {r.getVisibleCells().map((c) => <td key={c.id} className="px-3 first:pl-4 border-r border-hairline last:border-r-0 whitespace-nowrap">{flexRender(c.column.columnDef.cell, c.getContext())}</td>)}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {nSel > 0 && (
        <div className="sticky bottom-3 mx-auto w-fit flex items-center gap-2 bg-card border border-border rounded-card shadow-popover px-3 h-ctl-md text-ui-sm" data-testid={`${testid}-bulkbar`}>
          <span className="font-medium">{nSel} selected</span><span className="text-border">·</span>
          {bulkActions.map((a) => <Button key={a.label} size="sm" variant="ghost" onClick={() => a.onClick(Object.keys(selected).filter((k) => selected[k]))}>{a.label}</Button>)}
          <Button size="sm" variant="ghost" onClick={() => setSelected({})}>Esc</Button>
        </div>
      )}
    </div>
  );
}
